import React, { useState } from 'react';
import { ModalProps } from '../../../types/modal';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    sevenDayApi,
    useReassignBulkReservationsMutation,
    useCancelBulkReservationsMutation,
    useGetIndividualClientQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import FailedTable from './FailedTable';
import { useDispatch } from 'react-redux';
import { BulkActionResponse } from '../../../../app/Slices/SevenDayApiTypes';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import UserRole from '../../../../shared/AuthRoles';
import { User } from '../../../types/user';

interface BulkActionModalProps extends ModalProps {
    user: User;
    ids: number[];
    action?: 'cancel' | 'reassign';
}

const ReassignSchema = z.object({
    bookerId: z.number(),
});

const BulkActionModal = ({
    user,
    ids,
    action,
    open,
    onClose,
}: BulkActionModalProps) => {
    const loggedInUser = UserRole();
    const { data: guestUsers, isFetching: isFetchingGuestUsers } =
        useGetIndividualClientQuery(`/guests/${loggedInUser.company}/users`);
    const dispatch = useDispatch();

    const [reassignReservations] = useReassignBulkReservationsMutation();
    const [cancelReservations] = useCancelBulkReservationsMutation();

    const [response, setResponse] = useState<BulkActionResponse>();

    const Schema = action === 'reassign' ? ReassignSchema : z.object({});

    type Data = z.infer<typeof Schema>;

    const methods = useForm<Data>({
        defaultValues: { reason: '' },
        resolver: zodResolver(Schema),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods;

    const onSubmit = async (data: any) => {
        try {
            let response: BulkActionResponse | undefined;

            if (action === 'cancel') {
                response = await cancelReservations({
                    bookingIds: ids,
                    reason: 'User Disabled',
                }).unwrap();
            } else if (action === 'reassign') {
                response = await reassignReservations({
                    bookingIds: ids,
                    ...data,
                }).unwrap();
            }
            setResponse(response);
        } catch (error) {
            toast.error('Error performing bulk operation');
        }
    };

    const handleClose = () => {
        onClose();
        reset();
        setResponse(undefined);
        dispatch(sevenDayApi.util.invalidateTags(['Reservations']));
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {response ? (
                            <>
                                <DialogTitle>
                                    Results
                                    <IconButton
                                        onClick={handleClose}
                                        sx={{ color: 'text.primary' }}
                                        aria-label='close'
                                    >
                                        <Close />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent>
                                    <Stack spacing={2}>
                                        {response.succeeded.length > 0 && (
                                            <Typography>
                                                {`${
                                                    response.succeeded.length
                                                } reservations ${
                                                    action === 'reassign'
                                                        ? 'reassigned'
                                                        : 'cancelled'
                                                } successfully.`}
                                            </Typography>
                                        )}
                                        {response.failed.length > 0 && (
                                            <>
                                                <Typography
                                                    sx={{ fontWeight: 'bold' }}
                                                    gutterBottom
                                                >
                                                    The following reservations
                                                    were not{' '}
                                                    {action === 'reassign'
                                                        ? 'reassigned'
                                                        : 'cancelled'}
                                                    :
                                                </Typography>
                                                <FailedTable
                                                    data={response.failed}
                                                />
                                            </>
                                        )}
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        variant='contained'
                                        onClick={handleClose}
                                    >
                                        Continue
                                    </Button>
                                </DialogActions>
                            </>
                        ) : (
                            <>
                                <DialogTitle>
                                    Confirm Bulk Operation
                                    <IconButton
                                        onClick={handleClose}
                                        sx={{ color: 'text.primary' }}
                                        aria-label='close'
                                    >
                                        <Close />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent>
                                    <Stack spacing={1}>
                                        <Typography>
                                            Are you sure you want to {action}{' '}
                                            <b>{ids.length}</b> reservations?
                                        </Typography>
                                        {action === 'reassign' ? (
                                            <>
                                                <InputLabel required>
                                                    Primary Guest
                                                </InputLabel>
                                                <FormAutocomplete
                                                    name='bookerId'
                                                    control={control}
                                                    loading={
                                                        isFetchingGuestUsers
                                                    }
                                                    options={
                                                        isFetchingGuestUsers
                                                            ? []
                                                            : guestUsers
                                                                  .filter(
                                                                      ({
                                                                          isActive,
                                                                          id,
                                                                      }: User) =>
                                                                          isActive &&
                                                                          id !==
                                                                              user.id,
                                                                  )
                                                                  .map(
                                                                      ({
                                                                          id,
                                                                          firstName,
                                                                          lastName,
                                                                          email,
                                                                      }: User) => ({
                                                                          value: id,
                                                                          label: `${firstName} ${lastName} (${email})`,
                                                                      }),
                                                                  )
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <Alert severity='warning'>
                                                This action cannot be undone
                                            </Alert>
                                        )}
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <CustomButton
                                        color='neutral'
                                        variant='contained'
                                        onClick={handleClose}
                                    >
                                        Abort
                                    </CustomButton>
                                    <Button
                                        variant='contained'
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        Confirm
                                    </Button>
                                </DialogActions>
                            </>
                        )}
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default BulkActionModal;
